import { Injectable, inject } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    Category,
    CreateCategoryInput,
    DeleteCategoryInput,
    UpdateCategoryInput
} from '@app/core/services/api/API.service';
import { AmplifyApiService } from './amplify-api.service';

type CategoryGraphqlAuthMode = 'apiKey' | 'userPool';

@Injectable({
    providedIn: 'root'
})
export class CategoryService {
    private amplifyClient = generateClient();
    private _amplifyApiService = inject(AmplifyApiService);

    constructor() {}

    /**
     * Get category by ID
     */
    async getCategory(id: string): Promise<Category> {
        try {
            const category = await this.amplifyClient.graphql({
                query: queries.getCategory,
                variables: { id }
            });
            return category.data.getCategory as Category;
        } catch (error) {
            console.error('Error getting category:', error);
            return null;
        }
    }

    /**
     * List categories by parent category
     */
    async listCategoriesByParentCategory(
        parentId: string
    ): Promise<Category[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listCategoriesByParentCategory,
                    variables: {
                        parentId
                    }
                })
            ).data.listCategoriesByParentCategory;
            return data.items as Category[];
        } catch (error) {
            console.error(
                'Error listing categories by parent category:',
                error
            );
            return null;
        }
    }

    /**
     * List categories by product line
     */
    async listCategoriesByProductLine(
        productLine: string
    ): Promise<Category[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.ListCategoriesByProductLine,
                    variables: {
                        productLine
                    }
                })
            ).data.ListCategoriesByProductLine;
            return data.items as Category[];
        } catch (error) {
            console.error('Error listing categories by product line:', error);
            return null;
        }
    }

    /**
     * List categories by root and path
     */
    async listCategoriesByRootAndPath(rootId: string): Promise<Category[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.listCategoriesByRootAndPath,
                    variables: {
                        rootId
                    }
                })
            ).data.listCategoriesByRootAndPath;
            return data.items as Category[];
        } catch (error) {
            console.error('Error listing categories by root and path:', error);
            return null;
        }
    }

    /**
     * Obtiene una categoría por su campo `path` (GraphQL listCategoriesByPath).
     */
    async getCategoryByPath(
        path: string,
        authMode?: CategoryGraphqlAuthMode
    ): Promise<Category | null> {
        const normalizedPath = path?.trim();
        if (!normalizedPath) {
            return null;
        }

        try {
            const data = ((await this._amplifyApiService.graphql({
                query: queries.listCategoriesByPath,
                variables: {
                    path: normalizedPath,
                    limit: 1
                },
                ...(authMode ? { authMode } : {})
            })) as {
                data: {
                    listCategoriesByPath?: { items?: Category[] };
                };
            }).data.listCategoriesByPath;

            return (data?.items?.[0] as Category) ?? null;
        } catch (error) {
            console.error('Error getting category by path:', error);
            return null;
        }
    }

    /**
     * List categories by path
     * Only one category is returned
     */
    async listCategoriesByPath(path: string): Promise<Category> {
        return this.getCategoryByPath(path);
    }

    /**
     * List categories
     */
    async listCategories(): Promise<Category[]> {
        const categories: Category[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const data = (
                    await this.amplifyClient.graphql({
                        query: queries.listCategories,
                        variables: {
                            limit: 100,
                            nextToken: nextToken
                        }
                    })
                ).data.listCategories;

                if (data && data.items) {
                    categories.push(...(data.items as Category[]));
                }

                nextToken = data.nextToken;
            } while (nextToken !== null);

            return categories;
        } catch (error) {
            console.error('Error finding Categories:', error);
            return null;
        }
    }

    /**
     * Create category
     */
    async createCategory(input: CreateCategoryInput): Promise<Category> {
        try {
            const category = (
                await this.amplifyClient.graphql({
                    query: mutations.createCategory,
                    variables: {
                        input
                    }
                })
            ).data.createCategory;
            if (category) return category as Category;
        } catch (error) {
            console.error('Error creating category:', error);
            return null;
        }
    }

    /**
     * Update category
     */
    async updateCategory(input: UpdateCategoryInput): Promise<Category> {
        try {
            const category = (
                await this.amplifyClient.graphql({
                    query: mutations.updateCategory,
                    variables: {
                        input
                    }
                })
            ).data.updateCategory;
            if (category) return category as Category;
        } catch (error) {
            console.error('Error updating category:', error);
            return null;
        }
    }

    /**
     * Delete category
     */
    async deleteCategory(input: DeleteCategoryInput): Promise<Category> {
        try {
            const deleteCategory = (
                await this.amplifyClient.graphql({
                    query: mutations.deleteCategory,
                    variables: {
                        input
                    }
                })
            ).data.deleteCategory;
            return deleteCategory as Category;
        } catch {
            console.error('Error deleted category:', Error);
            return null;
        }
    }
}
